import { CloseButton } from '@components/shared/Dialog/CloseButton';
import type { CampaignType } from '@features/campaign-list';
import { useClients } from '@features/clients';
import { isFetchBaseQueryError } from '@utils/errors';
import { notifyError } from '@utils/flashMessage';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { getDateWindow } from '../../domain/date-window';
import type { BackendCampaignType } from '../../slice/api';
import { useCreateCrossChannelExportRequestMutation } from '../../slice/api';
import type { ExportCampaignRow } from './columns';
import {
  buildExportPayload,
  getExportErrorResult,
} from '../../domain/export-request';
import { ExportCampaignDataGrid } from './ExportCampaignDataGrid';
import { ExportFilters } from './ExportFilters';
import type { ExportFilterState } from './filter-state';
import { exportFilterInitialState } from './filter-state';
import { MAX_SELECTED_ROWS, MIN_SELECTED_ROWS } from './selection-limits';

const BACKEND_CAMPAIGN_TYPE: Partial<
  Record<CampaignType, BackendCampaignType>
> = {
  visitation: 'visitation',
  rsl: 'rsl_sales_only',
  rslvisits: 'rsl_visit',
};

type CrossChannelExportModalProps = {
  onClose: () => void;
  open: boolean;
};

export const CrossChannelExportModal = ({
  onClose: handleClose,
  open,
}: CrossChannelExportModalProps) => {
  const { clients, isLoading } = useClients();
  const [filterState, setFilterState] = useState<ExportFilterState>(
    exportFilterInitialState
  );
  const [selectedRows, setSelectedRows] = useState<ExportCampaignRow[]>([]);
  const [createExport, { isLoading: isSubmitting }] =
    useCreateCrossChannelExportRequestMutation();

  const effectiveClient = filterState.selectedClient ?? clients[0] ?? null;
  const effectiveAdvertiser =
    filterState.selectedAdvertiser ?? effectiveClient?.advertisers[0] ?? null;

  const resolvedFilterState: ExportFilterState = {
    ...filterState,
    selectedClient: effectiveClient,
    selectedAdvertiser: effectiveAdvertiser,
  };

  const dateWindow = getDateWindow(
    selectedRows.map(row => ({
      start: row.campaignStartDate,
      end: row.campaignEndDate,
    }))
  );

  const handleFilterChange = (newFilterState: ExportFilterState) => {
    setFilterState(newFilterState);
    setSelectedRows([]);
  };

  const handleCloseModal = () => {
    setSelectedRows([]);
    handleClose();
  };

  const handleExport = async () => {
    if (selectedRows.length === 0) return;

    const frontendType = selectedRows[0]?.campaignType ?? 'visitation';
    const backendType = BACKEND_CAMPAIGN_TYPE[frontendType];

    if (!backendType) {
      notifyError({ message: `Unsupported campaign type: ${frontendType}` });
      return;
    }

    const payload = buildExportPayload(selectedRows, dateWindow, backendType);

    try {
      await createExport(payload).unwrap();

      enqueueSnackbar(
        'Export requested. You will receive an email when the report is ready.',
        { variant: 'success' }
      );
      handleCloseModal();
    } catch (error) {
      if (!isFetchBaseQueryError(error)) {
        notifyError({
          message: 'Export request failed',
          consoleMessage: ['Cross-channel export error', error],
        });
        return;
      }

      const result = getExportErrorResult(error);
      if (result.type === 'snackbar') {
        enqueueSnackbar(result.message, { variant: result.variant });
      } else {
        notifyError({
          message: result.message,
          consoleMessage: result.consoleMessage,
        });
      }
    }
  };

  const isExportDisabled =
    selectedRows.length < MIN_SELECTED_ROWS ||
    selectedRows.length > MAX_SELECTED_ROWS ||
    isSubmitting;

  return (
    <Dialog fullWidth maxWidth="xl" onClose={handleCloseModal} open={open}>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        Cross-Channel Analysis Export
        <CloseButton onClick={handleCloseModal} />
      </DialogTitle>
      <DialogContent>
        <Alert severity="info">
          <Typography variant="body2">
            Select 2 to 5 campaigns with overlapping flight dates for your
            analysis. Your analysis will be delivered as a CSV file via email.
          </Typography>
        </Alert>
        <ExportFilters
          clients={clients}
          filterState={filterState}
          isLoading={isLoading}
          onFilterChange={handleFilterChange}
        />
        <ExportCampaignDataGrid
          dateWindow={dateWindow}
          filterState={resolvedFilterState}
          onSelectionChange={setSelectedRows}
          selectedRows={selectedRows}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', padding: 2 }}>
        <LoadingButton
          disabled={isExportDisabled}
          loading={isSubmitting}
          loadingPosition="start"
          onClick={() => void handleExport()}
          variant="contained"
        >
          {isSubmitting ? 'Submitting...' : 'Export to Email'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
